import React, { useState } from "react";
import { RefreshCw, Check, X, Wallet, ArrowRight, Loader2 } from "lucide-react";
import * as api from "../../api";

const bs = (value) => `Bs ${Math.round(Number(value || 0)).toLocaleString("es-VE")}`;

/**
 * Solicitud de REAJUSTE visible para el cliente: qué pidió, qué le proponen, la
 * diferencia y el saldo a favor o el monto adicional. Nada se aplica ni se acredita
 * hasta que pulsa ACEPTAR (con una confirmación explícita antes).
 */
export const AdjustmentDecisionCard = ({ order, onResolved, showToast }) => {
  const adj = order.adjustment || {};
  const [confirming, setConfirming] = useState(null); // "accept" | "reject" | null
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const original = adj.original || {};
  const proposal = adj.proposal || {};
  const surplus = Number(adj.surplusBs || 0);
  const extra = Number(adj.extraToPayBs || 0);

  const run = async (decision) => {
    setBusy(true);
    try {
      const updated = decision === "accept"
        ? await api.acceptOrderAdjustment(order.id)
        : await api.rejectOrderAdjustment(order.id, reason);
      showToast?.(decision === "accept"
        ? (surplus > 0 ? `Reajuste aceptado · ${bs(surplus)} acreditados a tu saldo`
          : "Reajuste aceptado")
        : "Reajuste rechazado: el equipo de AG7 te contactará", "success");
      await onResolved?.(updated);
    } catch (e) {
      showToast?.(e?.message || "No se pudo registrar tu decisión", "error");
    } finally {
      setBusy(false);
      setConfirming(null);
    }
  };

  return (
    <div className="rounded-xl border border-amber-500/40 bg-amber-950/25 p-3 space-y-3"
      data-testid={`adjustment-decision-${order.id}`}>
      <p className="text-amber-300 font-bold flex items-center gap-1.5 text-sm">
        <RefreshCw className="w-4 h-4" /> AG7 te propone un cambio en tu pedido
      </p>
      <p className="text-[12px] text-amber-100/90">{adj.note}</p>

      <div className="grid sm:grid-cols-2 gap-2">
        <div className="p-2 rounded-lg bg-ag7-bg border border-[var(--ag7-border-soft)]">
          <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Pediste</span>
          <p className="text-white text-sm font-bold" data-testid="adjustment-original-name">
            {original.packageName || order.packageName}
          </p>
          <p className="text-[11px] text-zinc-400">{original.gameName} · {bs(adj.originalPriceBs)}</p>
        </div>
        <div className="p-2 rounded-lg bg-ag7-bg border border-cyan-500/40">
          <span className="text-[10px] font-mono uppercase tracking-widest text-cyan-400 flex items-center gap-1">
            <ArrowRight className="w-3 h-3" /> Recibirías
          </span>
          <p className="text-white text-sm font-bold" data-testid="adjustment-proposal-name">
            {proposal.packageName || adj.newPackageName}
          </p>
          <p className="text-[11px] text-zinc-400">{proposal.gameName || adj.newGameName} · {bs(adj.newPriceBs)}</p>
        </div>
      </div>

      <div className="p-2 rounded-lg bg-ag7-bg border border-[var(--ag7-border-soft)] text-[11px] font-mono grid grid-cols-2 gap-1">
        <span className="text-zinc-500">Total del pedido antes:</span>
        <strong className="text-white text-right">{bs(adj.orderTotalsBefore?.priceBs ?? adj.originalPriceBs)}</strong>
        <span className="text-zinc-500">Total del pedido después:</span>
        <strong className="text-white text-right">{bs(adj.orderTotalsAfter?.priceBs ?? adj.newPriceBs)}</strong>
        <span className="text-zinc-500">Diferencia:</span>
        <strong className={`text-right ${surplus > 0 ? "text-emerald-300" : "text-amber-300"}`}
          data-testid="adjustment-difference">
          {surplus > 0 ? `- ${bs(surplus)}` : extra > 0 ? `+ ${bs(extra)}` : bs(0)}
        </strong>
      </div>

      {surplus > 0 && (
        <p className="text-[12px] text-emerald-300 flex items-center gap-1.5" data-testid="adjustment-surplus-note">
          <Wallet className="w-4 h-4" /> Al aceptar se acreditan {bs(surplus)} a tu saldo a favor.
        </p>
      )}
      {extra > 0 && (
        <p className="text-[12px] text-amber-300" data-testid="adjustment-extra-note">
          Al aceptar deberás pagar {bs(extra)} de diferencia para completar el pedido.
        </p>
      )}

      {confirming === "accept" ? (
        <div className="p-2 rounded-lg bg-black/40 border border-cyan-500/40 space-y-2">
          <p className="text-[12px] text-white">
            Confirma que aceptas recibir <strong>{proposal.packageName || adj.newPackageName}</strong>
            {surplus > 0 ? ` y ${bs(surplus)} a tu saldo` : extra > 0 ? ` y pagar ${bs(extra)}` : ""}.
          </p>
          <div className="flex gap-2">
            <button onClick={() => run("accept")} disabled={busy}
              data-testid={`confirm-accept-adjustment-${order.id}`}
              className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-2 rounded-lg text-xs inline-flex items-center justify-center gap-1.5 disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Sí, acepto
            </button>
            <button onClick={() => setConfirming(null)} disabled={busy}
              className="px-3 py-2 rounded-lg border border-[var(--ag7-border)] text-zinc-300 text-xs">
              Volver
            </button>
          </div>
        </div>
      ) : confirming === "reject" ? (
        <div className="p-2 rounded-lg bg-black/40 border border-red-500/40 space-y-2">
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
            data-testid={`reject-adjustment-reason-${order.id}`}
            placeholder="¿Por qué lo rechazas? (opcional)"
            className="w-full bg-ag7-bg border border-[var(--ag7-border)] rounded-lg p-2 text-xs text-white" />
          <div className="flex gap-2">
            <button onClick={() => run("reject")} disabled={busy}
              data-testid={`confirm-reject-adjustment-${order.id}`}
              className="flex-1 bg-red-600 hover:bg-red-500 text-white font-bold py-2 rounded-lg text-xs inline-flex items-center justify-center gap-1.5 disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />} Rechazar
            </button>
            <button onClick={() => setConfirming(null)} disabled={busy}
              className="px-3 py-2 rounded-lg border border-[var(--ag7-border)] text-zinc-300 text-xs">
              Volver
            </button>
          </div>
        </div>
      ) : (
        <div className="flex gap-2">
          <button onClick={() => setConfirming("accept")}
            data-testid={`accept-adjustment-${order.id}`}
            className="flex-1 bg-gradient-to-r from-emerald-600 to-cyan-600 hover:opacity-90 text-white font-bold py-2.5 rounded-xl text-xs inline-flex items-center justify-center gap-2">
            <Check className="w-4 h-4" /> ACEPTAR REAJUSTE
          </button>
          <button onClick={() => setConfirming("reject")}
            data-testid={`open-reject-adjustment-${order.id}`}
            className="px-4 py-2.5 rounded-xl border border-red-500/50 text-red-300 font-bold text-xs inline-flex items-center gap-2 hover:bg-red-500/10">
            <X className="w-4 h-4" /> Rechazar
          </button>
        </div>
      )}
    </div>
  );
};
